// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getLocalizedCurrency from "@insite/client-framework/Common/Utilities/getLocalizedCurrency";
import { getUnitListPrice, getUnitNetPrice } from "@insite/client-framework/Services/Helpers/ProductPriceService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import { ProductPriceDto } from "@insite/client-framework/Types/ApiModels";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";
import { fontConfig } from "@stoneage/XDL/constants";

type OwnProps = TypographyPresentationProps & {
    pricing: ProductPriceDto;
    showSavingsAmount: boolean;
    showSavingsPercent: boolean;
    currencySymbol: string;
    qtyOrdered?: number;
    extendedStyles?: ProductPriceSavingsMessageStyles;
    // XNG-Change
    showOnlySavingPercent?: boolean;
};

export interface ProductPriceSavingsMessageStyles {
    wrapper?: InjectableCss;
    messageWrapper?: InjectableCss;
    label?: TypographyPresentationProps;
    price?: TypographyPresentationProps;
    discount?: TypographyPresentationProps;
}

export const productPriceSavingsMessageStyles: ProductPriceSavingsMessageStyles = {
    wrapper: {
        css: css`
            display: flex;
            gap: 12px;
            white-space: nowrap;
        `,
    },
    messageWrapper: {
        css: css`
            display: flex;
            gap: 4px;
            align-items: baseline;
        `,
    },
    label: {
        ...fontConfig.xSmallSingleLine,
        weight: fontConfig.weight.regular,
        color: "text.main",
    },
    price: {
        ...fontConfig.xSmallSingleLine,
        color: "text.main",
        weight: fontConfig.weight.medium,
    },
    discount: {
        ...fontConfig.xSmallSingleLine,
        weight: fontConfig.weight.regular,
        color: "text.main",
    },
};

export const translatableMessages = {
    discountLabel: "MSRP",
    discountMessage: "Discount",
};

const mapStateToProps = (state: ApplicationState) => ({
    language: state.context.session.language,
});

type Props = ReturnType<typeof mapStateToProps> & OwnProps;

const ProductPriceSavingsMessage: FC<Props> = ({
    pricing,
    showSavingsAmount,
    showSavingsPercent,
    currencySymbol,
    qtyOrdered = 1,
    language,
    extendedStyles,
    // XNG-Change
    showOnlySavingPercent,
    ...otherProps
}) => {
    const [styles] = React.useState(() => mergeToNew(productPriceSavingsMessageStyles, extendedStyles));

    const { price: unitNetPrice } = getUnitNetPrice(pricing, qtyOrdered);
    const { price: unitListPrice, priceDisplay: unitListPriceDisplay } = getUnitListPrice(pricing, qtyOrdered);
    const hasPriceSavings = unitNetPrice < unitListPrice;
    if (!language) {
        return null;
    }

    if (showOnlySavingPercent && unitNetPrice === unitListPrice) {
        let savingPercent = "-%";
        if (unitNetPrice !== 0) {
            savingPercent = "0.0%";
        }
        return <>{savingPercent}</>;
    }

    if (!pricing || !hasPriceSavings) {
        return null;
    }
    const savingsAmount = Math.round((unitListPrice - unitNetPrice) * 100) / 100;
    const localizedSavingsAmount = getLocalizedCurrency({ amount: savingsAmount, language, currencySymbol });
    const savingsPercent = ((savingsAmount / unitListPrice) * 100).toFixed(1);

    // XNG-Change
    if (showOnlySavingPercent) {
        return <>{savingsPercent.toString()}%</>;
    }

    return (
        <StyledWrapper {...styles.wrapper} className="product-price-savings-message">
            <StyledWrapper data-test-selector="productPriceSavingsMessage" {...styles.messageWrapper}>
                <Typography {...styles.label}>{translate(translatableMessages.discountLabel)}</Typography>
                <Typography translate="no" {...styles.price}>
                    {unitListPriceDisplay}
                </Typography>
            </StyledWrapper>
            <Typography {...styles.discount}>
                {translate(translatableMessages.discountMessage)}&nbsp;
                <Typography translate="no">{savingsPercent.toString()}%</Typography>
            </Typography>
        </StyledWrapper>
    );
};

export default connect(mapStateToProps)(ProductPriceSavingsMessage);
